<template>
  <AppWrapper>
    <div class="privacy-container">
      <div class="header">
        <span class="grid-text-large">Privacy Policy</span>
      </div>
      <div class="content">
        <div class="last-updated">
          <span class="caption">Last updated: November 19, 2024</span>
        </div>

        <section class="policy-section">
          <h2 class="h2">Introduction</h2>
          <p class="body-text">
            Welcome to Gift a Habit ("we," "our," or "us"). We respect your
            privacy and are committed to protecting any information collected
            through our mobile application ("App") and website. This Privacy
            Policy explains our practices regarding data collection, use, and
            disclosure.
          </p>
        </section>

        <section class="policy-section">
          <h2 class="h2">Usage Analytics</h2>
          <p class="body-text">
            We use TelemetryDeck to collect anonymous usage data that helps us
            understand how our App and website are being used and how we can
            improve them. TelemetryDeck is privacy-focused and does not collect
            any personally identifiable information. You can read more about
            TelemetryDeck’s privacy policy at
            <a href="https://telemetrydeck.com/privacy"
              >https://telemetrydeck.com/privacy</a
            >
          </p>
          <h3 class="h3">Anonymous Usage Data</h3>
          <p class="body-text">
            Through both our App and website, we collect anonymous usage data
            that helps us understand how users interact with our service. This
            includes:
          </p>
          <ul class="policy-list">
            <li>Anonymous identifiers for habits and technical data</li>
            <li>Basic app configuration and usage statistics</li>
            <li>Anonymous habit tracking metrics and milestones</li>
            <li>Feature usage patterns</li>
            <li>App performance data</li>
          </ul>
          <p class="body-text">
            No data collected can be used to identify individual users.
          </p>
          <p class="body-text">This data is collected to:</p>
          <ul class="policy-list">
            <li>Understand how users interact with the App and website</li>
            <li>Improve the user experience</li>
            <li>Develop new features based on usage patterns</li>
            <li>Provide better habit-forming suggestions in future updates</li>
            <li>Identify and fix technical issues</li>
          </ul>
          <p class="body-text">All analytics data is:</p>
          <ul class="policy-list">
            <li>Collected in an anonymized form</li>
            <li>Cannot be traced back to individual users</li>
            <li>Does not contain any personally identifiable information</li>
            <li>Is not combined with other data to identify individuals</li>
            <li>
              Is collected through TelemetryDeck, which does not collect any
              data governed by privacy laws such as GDPR or CCPA
            </li>
          </ul>
        </section>

        <section class="policy-section">
          <h2 class="h2">Local and iCloud Storage</h2>
          <p class="body-text">All habit tracking data, including:</p>
          <ul class="policy-list">
            <li>Habit configurations</li>
            <li>Streak information</li>
            <li>Reward details</li>
            <li>Progress tracking</li>
          </ul>
          <p class="body-text">
            is stored locally on your device and synced with your private iCloud
            storage. This allows you to keep your habits synchronized across
            your Apple devices while maintaining privacy. We do not have access
            to this information.
          </p>
        </section>

        <section class="policy-section">
          <h2 class="h2">Data Sharing and Transfer</h2>
          <p class="body-text">
            We do not share, sell, rent, or trade any information with third
            parties for their commercial purposes because we don't collect
            personal information. The anonymous analytics data we collect is
            used solely for improving our services and is not shared with third
            parties except for TelemetryDeck, our analytics provider.
          </p>
        </section>

        <section class="policy-section">
          <h2 class="h2">Links to Other Websites</h2>
          <p class="body-text">
            Our App or website may contain links to other websites. We are not
            responsible for the privacy practices of other sites. We encourage
            our users to be aware when they leave our App or website and to read
            the privacy statements of any other site that collects personally
            identifiable information.
          </p>
        </section>

        <section class="policy-section">
          <h2 class="h2">Children's Privacy</h2>
          <p class="body-text">
            Gift a Habit is safe for use by children and families. Since we do
            not collect any personal information and all habit data is stored
            locally on the device, the app can be safely used by children under
            13 with parental supervision. We encourage parents and guardians to
            participate in their children's habit-forming journey using our app.
          </p>
        </section>

        <section class="policy-section">
          <h2 class="h2">Data Security</h2>
          <p class="body-text">
            While we don't collect personal data, we still take reasonable
            measures to protect the App and website against unauthorized access,
            alteration, disclosure, or destruction. The anonymous analytics data
            we collect is transmitted securely and stored in accordance with
            industry standards.
          </p>
        </section>

        <section class="policy-section">
          <h2 class="h2">Your Rights</h2>
          <p class="body-text">
            Since we don't collect personal data, there is no personal
            information for us to provide, correct, or delete. All your habit
            tracking data is stored locally on your device and remains under
            your control. The anonymous analytics data we collect cannot be
            linked back to individual users and therefore cannot be provided
            upon request.
          </p>
        </section>

        <section class="policy-section">
          <h2 class="h2">Changes to This Privacy Policy</h2>
          <p class="body-text">
            We may update our Privacy Policy from time to time. We will post the
            new Privacy Policy on this page and update the "Last updated" date
            at the top of this policy. You are advised to review this Privacy
            Policy periodically for any changes.
          </p>
        </section>

        <div class="copyright">
          <p class="body-text copyright-text">
            © 2024 Florian Vates. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  </AppWrapper>
</template>

<script setup>
import AppWrapper from "@/components/AppWrapper.vue";
</script>

<style scoped>
.privacy-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding-bottom: 60px;
}

.header {
  display: flex;
  flex-direction: column;
  justify-content: end;
  padding: 0 20px;
  height: 15vh;
  min-height: 80px;
  width: 100%;
  align-self: center;
}

.grid-text-large {
  margin-bottom: 20px;
}

.content {
  padding: 20px;
  width: 100%;
  box-sizing: border-box;
}

.last-updated {
  margin-bottom: 40px;
}

.policy-section {
  margin-bottom: 40px;
}

.h1 {
  margin-bottom: 20px;
  color: var(--Foreground---Primary);
}

.h2 {
  margin: 25px 0 15px;
  color: var(--Foreground---Primary);
}

.h3 {
  margin: 20px 0 10px;
  color: var(--Foreground---Primary);
}

.body-text {
  color: var(--Foreground---Secondary);
  font-family: Nunito;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 15px;
}

.policy-list {
  list-style-type: none;
  padding-left: 0;
  margin: 15px 0;
}

.policy-list li {
  color: var(--Foreground---Secondary);
  font-family: Nunito;
  font-size: 16px;
  line-height: 1.6;
  padding-left: 20px;
  position: relative;
  margin-bottom: 8px;
}

.policy-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: var(--Brand);
}

.policy-list.nested {
  margin: 8px 0 8px 20px;
}

.copyright {
  margin-top: 60px;
  padding-top: 20px;
  border-top: 1px solid var(--Border);
  width: 100%;
}

.copyright-text {
  text-align: center;
  color: var(--Foreground---Secondary);
  font-size: 14px;
}

/* Mobile Responsiveness */
@media only screen and (max-width: 680px) {
  .privacy-container {
    padding-bottom: 40px;
  }

  .header {
    background-color: var(--Brand);
  }

  .grid-text-large {
    color: #fff;
  }

  .content {
    padding: 15px;
  }

  .policy-section {
    margin-bottom: 30px;
  }

  .body-text,
  .policy-list li {
    font-size: 14px;
  }
}
</style>
